// Minimal raw-IndexedDB layer backing offline support. Three object stores:
// "captures" and "tasks" mirror the shape of their backend documents plus a
// local `cachedAt` timestamp (when this copy was last refreshed), and
// "mutationQueue" holds writes made while offline, replayed by syncEngine.js
// once the connection comes back.
const DB_NAME = "pmi_offline_db";
const DB_VERSION = 1;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("captures")) db.createObjectStore("captures", { keyPath: "id" });
      if (!db.objectStoreNames.contains("tasks")) db.createObjectStore("tasks", { keyPath: "id" });
      if (!db.objectStoreNames.contains("mutationQueue")) db.createObjectStore("mutationQueue", { keyPath: "seq", autoIncrement: true });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

export async function getAll(storeName) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(storeName, "readonly").objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getOne(storeName, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(storeName, "readonly").objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function putOne(storeName, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).put(value);
    tx.oncomplete = () => resolve(value);
    tx.onerror = () => reject(tx.error);
  });
}

export async function putMany(storeName, values) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    values.forEach((v) => store.put(v));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteOne(storeName, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Removes cached entries older than `maxAgeDays` (by `cachedAt`), skipping
// anything still referenced by a queued-but-unsynced mutation.
export async function pruneCache(storeName, maxAgeDays, keepIds) {
  const items = await getAll(storeName);
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
  const stale = items.filter((item) => !keepIds.has(item.id) && (item.cachedAt || 0) < cutoff);
  if (stale.length === 0) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    stale.forEach((item) => store.delete(item.id));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function addMutation(mutation) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("mutationQueue", "readwrite");
    const req = tx.objectStore("mutationQueue").add({ ...mutation, queuedAt: Date.now() });
    req.onsuccess = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
  });
}

export const getMutations = () => getAll("mutationQueue");
export const removeMutation = (seq) => deleteOne("mutationQueue", seq);
export const updateMutation = (mutation) => putOne("mutationQueue", mutation);
